import { injectable } from 'inversify';
import { createSelector } from '@reduxjs/toolkit';
import {
  PlayerRepository,
  TeamRepository,
} from '../../state/reduxRepositories';
import { AuthService } from '../../state/helperServices/auth/auth.service';
import { useAppSelector } from '../../state/hooks';
import { Incident } from '../../state/reduxRepositories/incident/incident.interface';
import { LoggedInUser } from '../../state/helperServices/auth/auth.interfaces';
import { PlayerIncidentList } from './incidentsList.interface';
import { IncidentListQueryService } from './incidentList.queryParam.service';
import { IncidentListPageQuery } from './incidentList.queryParams.interface';
import { Team } from '../../state/reduxRepositories/teams/team.interfaces';
import { IncidentDomain } from '../../domains/incident.domain';
import { Player } from '../../state/reduxRepositories/players/player.interfaces';

@injectable()
export class IncidentListDomainService {
  openIncidentByPlayerSelector: any;
  closedIncidentByPlayerSelector: any;
  orgOpenIncidentSelector: any;
  orgOpenIncidentByPlayerSelector: any;
  orgClosedIncidentByPlayerSelector: any;

  public constructor(
    private authService: AuthService,
    private incidentListQueryService: IncidentListQueryService,
    private teamRepository: TeamRepository,
    private incidentDomain: IncidentDomain,
    private playerRepository: PlayerRepository,
  ) {
    this.createOpenIncidentByPlayerSelector();
    this.createClosedIncidentByPlayerSelector();
    this.createOrgOpenIncidentSelector();
    this.createOrgOpenIncidentByPlayerSelector();
    this.createOrgClosedIncidentByPlayerSelector();
  }

  useUserSelector = (): LoggedInUser => {
    return useAppSelector(this.authService.loggedInUserSelector);
  };

  dispatchQueryParams = (queryParams: IncidentListPageQuery) => {
    this.incidentListQueryService.dispatchQueryParams(queryParams);
  };

  useOpenIncidentSelector = (): PlayerIncidentList[] => {
    return useAppSelector(this.incidentDomain.openIncidentSelector);
  };

  useOrgOpenIncidentByPlayerSelector = (): PlayerIncidentList[] => {
    return useAppSelector(this.orgOpenIncidentByPlayerSelector);
  };

  useOrgOpenIncidentSelector = (): PlayerIncidentList[] => {
    return useAppSelector(this.orgOpenIncidentSelector);
  };

  useOrgClosedIncidentByPlayerSelector = (): PlayerIncidentList[] => {
    return useAppSelector(this.orgClosedIncidentByPlayerSelector);
  };

  useOpenIncidentByPlayerSelector = (): PlayerIncidentList[] => {
    return useAppSelector(this.openIncidentByPlayerSelector);
  };

  useClosedIncidentByPlayerSelector = (): PlayerIncidentList[] => {
    return useAppSelector(this.closedIncidentByPlayerSelector);
  };

  useClosedIncidentSelector = (): PlayerIncidentList[] => {
    return useAppSelector(this.incidentDomain.closedIncidentSelector);
  };

  private getIncidentMaximumDatePerPlayer = (
    incidents: any,
    playerId?: string,
  ) => {
    const filter = incidents.filter(
      (incident: Incident) => incident.playerId === playerId,
    );
    const maxDate = Math.max(...filter.map((e: any) => new Date(e.createdAt)));
    return new Date(maxDate).getTime();
  };

  createOpenIncidentByPlayerSelector = () => {
    this.openIncidentByPlayerSelector = createSelector(
      this.incidentListQueryService.mergedQuerySelector,
      this.incidentDomain.openIncidentSelector,
      (queryParams, playerIncidentsList: PlayerIncidentList[]) => {
        // TODO: fix the NaN in the queryParam.slice and/or useQueryParam hook
        if (
          !queryParams?.selectedPlayerId ||
          queryParams?.selectedPlayerId === 'NaN'
        ) {
          return playerIncidentsList;
        }

        return playerIncidentsList.filter(
          (list) => queryParams?.selectedPlayerId === list?.playerId,
        );
      },
    );
  };

  createClosedIncidentByPlayerSelector = () => {
    this.closedIncidentByPlayerSelector = createSelector(
      this.incidentListQueryService.mergedQuerySelector,
      this.incidentDomain.closedIncidentSelector,
      (queryParams, playerIncidentsList: PlayerIncidentList[]) => {
        // TODO: fix the NaN in the queryParam.slice and/or useQueryParam hook
        if (
          !queryParams?.selectedPlayerId ||
          queryParams?.selectedPlayerId === 'NaN'
        ) {
          return playerIncidentsList;
        }

        return playerIncidentsList.filter(
          (list) => queryParams?.selectedPlayerId === list?.playerId,
        );
      },
    );
  };

  createOrgClosedIncidentByPlayerSelector = () => {
    this.orgClosedIncidentByPlayerSelector = createSelector(
      this.incidentListQueryService.mergedQuerySelector,
      this.playerRepository.selectors.resourcesSelector,
      this.incidentDomain.closedIncidentSelector,
      this.authService.loggedInUserSelector,
      this.teamRepository.selectors.resourcesSelector,
      (
        queryParams,
        players: Player[],
        closedIncidents: Incident[],
        user: LoggedInUser,
        teams: Team[],
      ) => {
        const orgPlayers: Player[] = [];
        const orgUserTeams = teams.filter(
          (team) =>
            team.teamManagerUserIds.includes(user.id) &&
            team.playerIds.length > 0,
        );

        const mapOrgPlayersByTeams = orgUserTeams.map((orgUserTeam: Team) => {
          return players.filter((player) =>
            orgUserTeam.playerIds.includes(player.id),
          );
        });

        mapOrgPlayersByTeams.map((mapOrgPlayersByTeam) => {
          return mapOrgPlayersByTeam.map((player: Player) => {
            if (!orgPlayers.some((orgPlayer) => orgPlayer?.id === player?.id)) {
              orgPlayers.push(player);
            }
            return player;
          });
        });

        orgPlayers.map((orgPlayer) => {
          return closedIncidents.filter((closedIncident: Incident) => {
            return closedIncident.playerId === orgPlayer?.id;
          });
        });

        const orgPlayersClosedIncident = orgPlayers.map((orgPlayer) => {
          const incidents = closedIncidents.find(
            (incident: Incident) => incident.playerId === orgPlayer?.id,
          );
          return incidents;
        });

        if (
          !queryParams?.selectedPlayerId ||
          queryParams?.selectedPlayerId === 'NaN'
        ) {
          return orgPlayersClosedIncident.filter(
            (incident) => incident !== undefined,
          );
        }
        return orgPlayersClosedIncident.filter(
          (incident) =>
            incident !== undefined &&
            incident.playerId === queryParams?.selectedPlayerId,
        );
      },
    );
  };

  createOrgOpenIncidentByPlayerSelector = () => {
    this.orgOpenIncidentByPlayerSelector = createSelector(
      this.incidentListQueryService.mergedQuerySelector,
      this.playerRepository.selectors.resourcesSelector,
      this.incidentDomain.openIncidentSelector,
      this.authService.loggedInUserSelector,
      this.teamRepository.selectors.resourcesSelector,
      (
        queryParams,
        players: Player[],
        openIncidents: Incident[],
        user: LoggedInUser,
        teams: Team[],
      ) => {
        const orgPlayers: Player[] = [];
        const orgUserTeams = teams.filter(
          (team) =>
            team.teamManagerUserIds.includes(user.id) &&
            team.playerIds.length > 0,
        );

        const mapOrgPlayersByTeams = orgUserTeams.map((orgUserTeam: Team) => {
          return players.filter((player) =>
            orgUserTeam.playerIds.includes(player.id),
          );
        });

        mapOrgPlayersByTeams.map((mapOrgPlayersByTeam) => {
          return mapOrgPlayersByTeam.map((player: Player) => {
            if (!orgPlayers.some((orgPlayer) => orgPlayer?.id === player?.id)) {
              orgPlayers.push(player);
            }
            return player;
          });
        });

        orgPlayers.map((orgPlayer) => {
          return openIncidents.filter((openIncident: Incident) => {
            return openIncident.playerId === orgPlayer?.id;
          });
        });

        const orgPlayersOpenIncident = orgPlayers.map((orgPlayer) => {
          const incidents = openIncidents.find(
            (incident: Incident) => incident.playerId === orgPlayer?.id,
          );
          return incidents;
        });
        if (
          !queryParams?.selectedPlayerId ||
          queryParams?.selectedPlayerId === 'NaN'
        ) {
          return orgPlayersOpenIncident.filter(
            (incident) => incident !== undefined,
          );
        }
        return orgPlayersOpenIncident.filter(
          (incident) =>
            incident !== undefined &&
            incident.playerId === queryParams?.selectedPlayerId,
        );
      },
    );
  };

  createOrgOpenIncidentSelector = () => {
    this.orgOpenIncidentSelector = createSelector(
      this.playerRepository.selectors.resourcesSelector,
      this.incidentDomain.openIncidentSelector,
      this.authService.loggedInUserSelector,
      this.teamRepository.selectors.resourcesSelector,
      (
        players: Player[],
        openIncidents: Incident[],
        user: LoggedInUser,
        teams: Team[],
      ) => {
        const orgPlayers: Player[] = [];
        const orgUserTeams = teams.filter(
          (team) =>
            team.teamManagerUserIds.includes(user.id) &&
            team.playerIds.length > 0,
        );

        const mapOrgPlayersByTeams = orgUserTeams.map((orgUserTeam: Team) => {
          return players.filter((player) =>
            orgUserTeam.playerIds.includes(player.id),
          );
        });

        mapOrgPlayersByTeams?.map((mapOrgPlayersByTeam) => {
          return mapOrgPlayersByTeam?.map((player: Player) => {
            if (!orgPlayers.some((orgPlayer) => orgPlayer?.id === player?.id)) {
              orgPlayers.push(player);
            }
            return player;
          });
        });

        const orgOpenIncidentsFilter = orgPlayers.map(
          (mapOrgPlayersByTeam, key) => {
            const filterIncidents = openIncidents.find(
              (incident: Incident) =>
                incident.playerId === mapOrgPlayersByTeam?.id,
            );

            return filterIncidents;
          },
        );
        return orgOpenIncidentsFilter.filter((filter) => filter !== undefined);
      },
    );
  };
}
